/**
 * LiveAvailabilityStrip — thin, always-visible header banner showing the
 * next bookable slot from WriteUpp.
 *
 * Behaviour:
 *  - On the homepage the banner rotates between three category views
 *    (any / TMJ / massage) every ~7 seconds so visitors get a live feel
 *    of what's available across the whole clinic without seeing an
 *    overwhelming list.
 *  - On a service page the banner locks to the category owned by that
 *    page (TMJ for tinnitus/headache/facial-pain/TMD, massage for the
 *    massage page). On any other route the category-agnostic "any" view
 *    is used.
 *  - Data refreshes every 60 seconds; the backend caches for 10 minutes
 *    so this is cheap and won't rate-limit the WriteUpp API.
 *  - Clicking the strip opens the booking modal deep-linked to whichever
 *    category is showing in the strip at that moment.
 *  - Dismissible for the current browser session (sessionStorage).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { CalendarClock, X, Sparkles } from 'lucide-react';
import { useBookingModal, CATEGORY_META } from '../context/BookingContext';
import { NEXT_SLOT_FEATURE } from '../config/featureFlags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'livestrip_dismissed_v1';
const REFRESH_MS = 60_000;    // hit the backend at most every minute
const ROTATE_MS  = 7_000;     // homepage rotation cadence

// Routes that shouldn't render the strip at all — admin, thank-you, and
// the /legal/:slug policy pages where a live booking hook feels out of place.
const HIDDEN_PATH_PREFIXES = ['/admin', '/thank-you', '/legal'];

// Service slug → booking category (mirrors ServicePage.jsx). If we grow
// more services, this list should stay in sync.
const SERVICE_SLUG_TO_CATEGORY = {
  'tmd-treatment': 'tmj',
  'headache-migraine-relief': 'tmj',
  'tinnitus-management': 'tmj',
  'facial-pain-treatment': 'tmj',
  'massage': 'massage',
};

// Homepage rotation — first is category-agnostic "any next slot".
const HOMEPAGE_ROTATION = [null, 'tmj', 'massage'];

const useDismissed = () => {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const dismiss = useCallback(() => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  }, []);
  return [dismissed, dismiss];
};

const LiveAvailabilityStrip = () => {
  const location = useLocation();
  const { openBooking } = useBookingModal();
  const [dismissed, dismiss] = useDismissed();

  // Compute which categories to show for the current route.
  const activeCategories = useMemo(() => {
    const p = location.pathname;
    if (HIDDEN_PATH_PREFIXES.some((prefix) => p.startsWith(prefix))) return null;
    if (p === '/' || p === '') return HOMEPAGE_ROTATION;
    const svcMatch = p.match(/^\/services\/([^/]+)/);
    if (svcMatch) {
      const cat = SERVICE_SLUG_TO_CATEGORY[svcMatch[1]] || null;
      return [cat];
    }
    return [null];  // knowledge base / videos / etc.
  }, [location.pathname]);

  // Cache the fetched slot per category so rotation swaps instantly.
  const [slotsByCategory, setSlotsByCategory] = useState({});
  const [rotationIndex, setRotationIndex] = useState(0);
  const refreshTimerRef = useRef(null);
  const rotateTimerRef = useRef(null);
  // Ref to the rendered strip so we can measure its real height and push
  // the exact value into --live-strip-h (avoids 2px whitespace on mobile).
  const stripRef = useRef(null);

  const currentCategory = activeCategories ? activeCategories[rotationIndex % activeCategories.length] : null;

  // Fetch the slot for a given category, keep failures out of the UI.
  const fetchFor = useCallback(async (cat) => {
    try {
      const res = await axios.get(`${API}/booking/next-slot`, { params: cat ? { category: cat } : {} });
      if (res.data?.available) {
        setSlotsByCategory((m) => ({ ...m, [cat || '_any']: res.data }));
      } else {
        setSlotsByCategory((m) => {
          const next = { ...m };
          delete next[cat || '_any'];
          return next;
        });
      }
    } catch {
      // Swallow — endpoint failures should hide the strip gracefully.
    }
  }, []);

  // On category-set change (route change), fetch everything and reset rotation.
  useEffect(() => {
    if (!NEXT_SLOT_FEATURE) return undefined;
    if (!activeCategories) return undefined;
    setRotationIndex(0);
    activeCategories.forEach((cat) => fetchFor(cat));
    // Poll — cheap because backend is 10-min cached
    refreshTimerRef.current = setInterval(() => {
      activeCategories.forEach((cat) => fetchFor(cat));
    }, REFRESH_MS);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [activeCategories, fetchFor]);

  // Rotation timer — only if we have >1 category to cycle through and at
  // least the first one succeeded so we don't blink on empty state.
  useEffect(() => {
    if (!NEXT_SLOT_FEATURE) return undefined;
    if (!activeCategories || activeCategories.length <= 1) return undefined;
    rotateTimerRef.current = setInterval(() => {
      setRotationIndex((i) => (i + 1) % activeCategories.length);
    }, ROTATE_MS);
    return () => {
      if (rotateTimerRef.current) clearInterval(rotateTimerRef.current);
    };
  }, [activeCategories]);

  // Push --live-strip-h into the CSS custom property so the fixed
  // header and every page's padding-top react. Cleared on unmount /
  // dismissal / route-hidden so the layout snaps back cleanly.
  useEffect(() => {
    const visible = NEXT_SLOT_FEATURE && !!activeCategories && !dismissed && !!slotsByCategory[currentCategory || '_any'];
    const isMobile = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 640px)').matches;
    const px = visible ? (isMobile ? '48px' : '58px') : '0px';
    document.documentElement.style.setProperty('--live-strip-h', px);
    return () => {
      document.documentElement.style.setProperty('--live-strip-h', '0px');
    };
  }, [activeCategories, dismissed, slotsByCategory, currentCategory]);

  // Feature parked (see /app/frontend/src/config/featureFlags.js). Flip
  // NEXT_SLOT_FEATURE back to `true` to re-enact — every hook above stays
  // wired so re-enacting is a one-line change.
  if (!NEXT_SLOT_FEATURE) return null;

  if (!activeCategories || dismissed) return null;

  const slot = slotsByCategory[currentCategory || '_any'];
  const meta = currentCategory ? CATEGORY_META[currentCategory] : null;

  // Fail-quiet: if no slot yet loaded or all requests failed for the
  // current view, render nothing (never a jarring empty banner).
  if (!slot) return null;

  const firstName = (slot.practitioner || '').split(' ')[0];
  const label = meta?.label || 'availability';

  const handleClick = () => {
    openBooking(currentCategory
      ? { category: currentCategory, source: 'live-strip' }
      : { source: 'live-strip' });
  };

  return (
    <div
      ref={stripRef}
      className="live-strip"
      role="region"
      aria-label="Live clinic availability"
      data-testid="live-availability-strip"
    >
      <button
        type="button"
        className="live-strip-body"
        onClick={handleClick}
        data-testid="live-strip-cta"
        aria-label={`Book next ${label} slot: ${slot.when_human}${firstName ? ` with ${firstName}` : ''}`}
      >
        <span className="live-strip-pulse" aria-hidden />
        <span className="live-strip-icon" aria-hidden><CalendarClock size={22} /></span>
          <span className="live-strip-copy" key={`${currentCategory || 'any'}-${slot.when_human}`}>
            <span className="live-strip-lead">
              Next {currentCategory ? `${label} ` : ''}slot
            </span>
            <span className="live-strip-sep" aria-hidden>·</span>
            <span className="live-strip-when">
              {slot.when_human}
              {firstName ? <span className="live-strip-who"> with {firstName}</span> : null}
            </span>
            {(slot.duration_min || slot.price_gbp) && (
              <>
                <span className="live-strip-sep" aria-hidden>·</span>
                <span className="live-strip-fit">
                  {slot.duration_min ? `${slot.duration_min} min` : ''}
                  {slot.duration_min && slot.price_gbp ? ' · ' : ''}
                  {slot.price_gbp ? `${currentCategory ? '' : 'from '}£${Math.round(slot.price_gbp)}` : ''}
                </span>
              </>
            )}
          </span>
        <span className="live-strip-book">
          <Sparkles size={18} aria-hidden />
          <span>Book</span>
        </span>
      </button>
      <button
        type="button"
        className="live-strip-dismiss"
        onClick={dismiss}
        aria-label="Dismiss availability banner"
        data-testid="live-strip-dismiss"
        title="Hide until next visit"
      >
        <X size={22} />
      </button>
    </div>
  );
};

export default LiveAvailabilityStrip;
